/**
 * strategyHelpers.js — pure utility functions for strategy data parsing.
 *
 * Shared by StrategyDetail (read-only display) and potentially by
 * the strategy form (validation). No React dependencies — these
 * are simple parse-and-validate helpers.
 */

/**
 * Defensive parse of trailing_stop_loss from the strategy object.
 * Mirrors the backend's parseTrailingStopLoss contract exactly —
 * malformed / out-of-range values become null (= "TSL disabled")
 * rather than crashing the detail page. The same 0.1–50 range
 * the validator enforces is re-checked here so the view layer
 * never trusts a bad row.
 */
export function parseTrailingStopLossConfig(cfg) {
  if (cfg == null) return null;
  let parsed = cfg;
  if (typeof cfg === 'string') {
    try { parsed = JSON.parse(cfg); } catch { return null; }
  }
  if (!parsed || typeof parsed !== 'object') return null;

  if (parsed.type === 'fixed') {
    const value = Number(parsed.value);
    if (!Number.isFinite(value) || value < 0.1 || value > 50) return null;
    return { type: 'fixed', value };
  }

  if (parsed.type === 'percent') {
    const value = Number(parsed.value);
    if (!Number.isFinite(value) || value < 0.1 || value > 50) return null;
    return { type: 'percent', value };
  }

  if (parsed.type === 'stepped') {
    const initial = Number(parsed.initial_stop_pct);
    const hurdle = Number(parsed.hurdle_pct);
    const step = Number(parsed.step_pct);
    if (!Number.isFinite(initial) || !Number.isFinite(hurdle) || !Number.isFinite(step)) return null;
    return { type: 'stepped', initial_stop_pct: initial, hurdle_pct: hurdle, step_pct: step };
  }

  return null;
}

/**
 * Defensive parse of the target (take-profit) config. Mirrors the
 * backend targetSchema (percent 0.1–500, price > 0). Returns null for
 * anything malformed so the detail page never trusts a bad row.
 */
export function parseTargetConfig(cfg) {
  if (cfg == null) return null;
  let parsed = cfg;
  if (typeof cfg === 'string') {
    try { parsed = JSON.parse(cfg); } catch { return null; }
  }
  if (!parsed || typeof parsed !== 'object') return null;

  if (parsed.type === 'percent') {
    const value = Number(parsed.value);
    if (!Number.isFinite(value) || value < 0.1 || value > 500) return null;
    return { type: 'percent', value };
  }

  if (parsed.type === 'price') {
    const value = Number(parsed.value);
    if (!Number.isFinite(value) || value <= 0) return null;
    return { type: 'price', value };
  }

  return null;
}
